
import { defineComponent, onMounted, ref, computed } from "vue";
import { useRouter } from "vue-router";
import { StepperComponent } from "@/assets/ts/components/_StepperComponent";
import { useForm } from "vee-validate";
import { Field, ErrorMessage } from "vee-validate";
import { hideModal, showModal } from "@/core/helpers/dom";
import { ViaCEP } from "viacep";
import Swal from "sweetalert2/dist/sweetalert2.min.js";
import * as Yup from "yup";
import useEmitter from "@/composables/useEmitter";
import { useStore } from "vuex";

import { Subscribe } from "@/core/services/consensu/subscribe";

interface Step1 {
  planName: string;
  planType: string;
}

interface Step2 {
  name: string;
  cpfCnpj: string;
  phone: string;
}

interface Step3 {
  zipCode: string;
  number: string;
  complement: string;
  state: string;
  city: string;
  district: string;
  street: string;
}

interface KTSelectPlan extends Step1, Step2, Step3 {}

interface Options {
  month: {
    id: string;
    price: string;
    sequence: string;
    name: string;
  };
  yearly: {
    id: string;
    price: string;
    sequence: string;
    name: string;
  };
}

export default defineComponent({
  name: "select-plan-modal",
  components: {
    Field,
    ErrorMessage,
  },
  setup() {
    const router = useRouter();
    const store = useStore();
    const loading = ref(false);
    const _stepperObj = ref<StepperComponent | null>(null);
    const createAppRef = ref<HTMLElement | null>(null);
    const selectPlanRef = ref<HTMLElement | null>(null);
    const Options = ref<Options | null>({
      month: {
        id: "",
        price: "",
        sequence: "",
        name: "",
      },
      yearly: {
        id: "",
        price: "",
        sequence: "",
        name: "",
      },
    });

    const owner = computed(() => {
      return store.getters.currentUser;
    });
    const currentStepIndex = ref(0);
    const emitter = useEmitter();
    const formData = ref<KTSelectPlan>({
      planName: "",
      planType: "",
      name: owner.value.name || "",
      cpfCnpj: owner.value.cpf_cnpj || "",
      phone: owner.value.phone || "",
      zipCode: owner.value?.address?.zip_code || "",
      number: owner.value?.address?.number || "",
      complement: owner.value?.address?.complement || "",
      state: owner.value?.address?.state || "",
      district: owner.value?.address?.district || "",
      street: owner.value?.address?.street || "",
      city: owner.value?.address?.city || "",
    });

    onMounted(() => {
      emitter.on("select-plan", (payload) => {
        formData.value.planName = payload.name;
        formData.value.planType = payload.type;
        Options.value = payload.options;
        showModal(selectPlanRef.value);
      });
      _stepperObj.value = StepperComponent.createInsance(
        createAppRef.value as HTMLElement
      );
    });

    const Schema = [
      Yup.object({
        planName: Yup.string()
          .required("Você precisa selecionar um plano.")
          .label("Plano selecionado"),
        planType: Yup.string()
          .required("Você precisa escolher uma recorrência.")
          .label("Recorrência"),
      }),
      Yup.object({
        name: Yup.string()
          .required("Nome é um campo obrigatório")
          .label("Nome Completo"),
        cpfCnpj: Yup.string()
          .required("CPF/CNPJ é um campo obrigatório")
          .label("CPF ou CNPJ"),
        phone: Yup.string()
          .required("Telefone é um campo obrigatório")
          .label("Telefone"),
      }),
      Yup.object({
        zipCode: Yup.string()
          .required("CEP é um campo obrigatório")
          .label("Cep"),
        number: Yup.string()
          .required("Número é um campo obrigatório")
          .label("Número"),
        complement: Yup.string().label("Complemento"),
        state: Yup.string()
          .required("Estado é um campo obrigatório")
          .label("Estado"),
        city: Yup.string()
          .required("Cidade é um campo obrigatório")
          .label("Cidade"),
        district: Yup.string()
          .required("Bairro é um campo obrigatório")
          .label("Bairro"),
        street: Yup.string()
          .required("Logradouro é um campo obrigatório")
          .label("Logradouro"),
      }),
    ];

    // extracts the individual step schema
    const currentSchema = computed(() => {
      return Schema[currentStepIndex.value];
    });

    const totalSteps = computed(() => {
      if (!_stepperObj.value) {
        return;
      }

      return _stepperObj.value.totatStepsNumber;
    });

    const { resetForm, handleSubmit } = useForm<Step1 | Step2 | Step3>({
      validationSchema: currentSchema,
    });

    const previousStep = () => {
      if (!_stepperObj.value) {
        return;
      }

      currentStepIndex.value--;

      _stepperObj.value.goPrev();
    };

    const handleStep = handleSubmit((values) => {
      formData.value = {
        ...formData.value,
        ...values,
      };
      console.log("handleStep", { values });
      currentStepIndex.value++;
      if (!_stepperObj.value) {
        return;
      }

      _stepperObj.value.goNext();
    });

    const formSubmit = async () => {
      loading.value = true;
      let data = {
        data: formData.value,
        plan: Options.value ? Options.value[formData.value.planType]?.id : null,
      };

      await Subscribe(data).then((res) => {
        if (res.data) {
          loading.value = false;
          let invoice = res.data.transaction
            ? res.data.transaction.recent_invoices[0]
            : false;
          Swal.fire({
            title: "Plano selecionado com sucesso!",
            html: `
                <p>
                  <h3>Aguardando Pagamento!</h3> <br />
                  <b>Observação:</b> 
                  Após o pagamento levará em média 5 à 10 minutos, para a solução de pagamentos <i>IUGU</i>, dar baixa e enviar a confirmação para nossa central.
                </p>
                `,
            icon: "success",
            buttonsStyling: false,
            confirmButtonText: "Ir para Fatura",
            customClass: {
              confirmButton: "btn fw-bold btn-light-primary",
            },
          }).then(() => {
            hideModal(selectPlanRef.value);
            loading.value = false;
            _stepperObj.value?.goFirst();
            currentStepIndex.value = 0;
            resetForm();
            setTimeout(() => {
              router.push("/invoices");
              if (invoice.secure_url) {
                window.open(invoice.secure_url, "_blank");
              }
            }, 500);
          });
        }
      });
    };

    // use viaCep module to get the address from zipCode value
    const getAddress = async () => {
      const zipCode = formData.value.zipCode.replace(/\D/g, "");
      if (zipCode.length !== 8) {
        formData.value = {
          ...formData.value,
          street: "",
          district: "",
          city: "",
          state: "",
        };
        return;
      }
      const via = new ViaCEP();
      const response = await via.cep(zipCode);
      formData.value = {
        ...formData.value,
        street: response.logradouro,
        district: response.bairro,
        city: response.localidade,
        state: response.uf,
      };
    };

    return {
      owner,
      getAddress,
      Options,
      formData,
      handleStep,
      formSubmit,
      previousStep,
      createAppRef,
      currentStepIndex,
      totalSteps,
      selectPlanRef,
      loading,
    };
  },
});
